import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaUndo, FaList, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';

const initialFormState = {
  code: '',
  scope: 'global',
  course_id: '',
  discount_type: 'percentage',
  discount_value: '',
  start_date: '',
  end_date: '',
  usage_limit: '',
  limit_per_user: 1,
  status: 'active'
};

const VoucherManagement = () => {
  const navigate = useNavigate();
 const [vouchers, setVouchers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchVouchers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.vouchers.list();
      if (response.data?.success && Array.isArray(response.data.data)) {
        setVouchers(response.data.data);
      } else if (Array.isArray(response.data)) {
        setVouchers(response.data);
      } else {
        setVouchers([]);
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      toast.error(error.response?.data?.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await api.courses.getAll(true);
      if (response.data?.success && Array.isArray(response.data.data)) {
        setCourses(response.data.data);
      } else if (Array.isArray(response.data)) {
        setCourses(response.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  }, []);

  useEffect(() => {
    fetchVouchers();
    fetchCourses();
  }, [fetchVouchers, fetchCourses]);

  const handleShowModal = (voucher = null) => {
    if (voucher) {
      setEditingVoucher(voucher);
      setFormState({
        code: voucher.code || '',
        scope: voucher.scope || 'global',
        course_id: voucher.course_id ? String(voucher.course_id) : '',
        discount_type: voucher.discount_type || 'percentage',
        discount_value: voucher.discount_value != null ? String(voucher.discount_value) : '',
        start_date: voucher.start_date ? voucher.start_date.substring(0, 16) : '',
        end_date: voucher.end_date ? voucher.end_date.substring(0, 16) : '',
        usage_limit: voucher.usage_limit != null ? String(voucher.usage_limit) : '',
        limit_per_user: voucher.limit_per_user || 1,
        status: voucher.status || 'active'
      });
    } else {
      setEditingVoucher(null);
      setFormState(initialFormState);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (submitting) return;
    setShowModal(false);
    setEditingVoucher(null);
    setFormErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formState.code.trim()) errors.code = 'Code is required';
    if (!formState.discount_type) errors.discount_type = 'Discount type is required';
    if (!formState.discount_value) errors.discount_value = 'Discount value is required';
    else if (isNaN(Number(formState.discount_value)) ||	Number(formState.discount_value) <= 0)
      errors.discount_value = 'Discount value must be positive';
    else if (formState.discount_type === 'percentage' && (Number(formState.discount_value) <= 0 || Number(formState.discount_value) > 100))
      errors.discount_value = 'Percentage must be between 1 and 100';

    if (!formState.start_date) errors.start_date = 'Start date is required';
    if (!formState.end_date) errors.end_date = 'End date is required';
    if (formState.start_date && formState.end_date) {
      const start = new Date(formState.start_date);
      const end = new Date(formState.end_date);
      if (end <= start) errors.end_date = 'End date must be after start date';
    }

    if (formState.scope === 'course' && !formState.course_id)
      errors.course_id = 'Course is required for course-scoped vouchers';

    if (formState.usage_limit) {
      const usage = parseInt(formState.usage_limit, 10);
      if (isNaN(usage) || usage <= 0) errors.usage_limit = 'Usage limit must be a positive integer';
    }

    if (!formState.limit_per_user) errors.limit_per_user = 'Per user limit is required';
    else {
      const perUser = parseInt(formState.limit_per_user, 10);
      if (isNaN(perUser) || perUser <= 0) errors.limit_per_user = 'Per user limit must be a positive integer';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const payload = {
      code: formState.code.trim(),
      scope: formState.scope,
      course_id: formState.scope === 'course' && formState.course_id ? parseInt(formState.course_id, 10) : null,
      discount_type: formState.discount_type,
      discount_value: Number(formState.discount_value),
      start_date: new Date(formState.start_date).toISOString(),
      end_date: new Date(formState.end_date).toISOString(),
      usage_limit: formState.usage_limit ? parseInt(formState.usage_limit, 10) : null,
      limit_per_user: parseInt(formState.limit_per_user, 10),
      status: formState.status
    };

    setSubmitting(true);
    try {
      if (editingVoucher) {
        await api.vouchers.update(editingVoucher.id, payload);
        toast.success('Voucher updated successfully');
      } else {
        await api.vouchers.create(payload);
        toast.success('Voucher created successfully');
      }
      await fetchVouchers();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving voucher:', error);
      toast.error(error.response?.data?.message || 'Failed to save voucher');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (voucher) => {
    setVoucherToDelete(voucher);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!voucherToDelete) return;
    try {
      setDeleteLoading(true);
      await api.vouchers.remove(voucherToDelete.id);
      toast.success('Voucher deleted successfully');
      await fetchVouchers();
      setShowDeleteModal(false);
      setVoucherToDelete(null);
    } catch (error) {
      console.error('Error deleting voucher:', error);
      toast.error(error.response?.data?.message || 'Failed to delete voucher');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setVoucherToDelete(null);
  };

  const filteredVouchers = useMemo(() => {
    return vouchers.filter((voucher) => {
      const matchesSearch = !searchTerm.trim() ||
        voucher.code?.toLowerCase().includes(searchTerm.trim().toLowerCase());

      let matchesStatus = true;
      if (statusFilter === 'active') matchesStatus = voucher.status === 'active' && !voucher.deleted;
      else if (statusFilter === 'inactive') matchesStatus = voucher.status === 'inactive' && !voucher.deleted;
      else if (statusFilter === 'deleted') matchesStatus = !!voucher.deleted;

      return matchesSearch && matchesStatus;
    });
  }, [vouchers, searchTerm, statusFilter]);

  const getCourseName = (courseId) => {
    if (!courseId) return '-';
    const course = courses.find((c) => c.id === courseId);
    return course ? course.name || course.title : `Course #${courseId}`;
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Manage Vouchers
          </h5>
        </div>
      </div>

      {/* Search + Filter + Add */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-2">
          <div className="flex shadow-sm rounded-lg sm:rounded-xl overflow-hidden bg-white">
            <div className="flex items-center px-3 bg-white border-r border-gray-200">
              <FaSearch className="w-4 h-4 text-purple-600" />
            </div>
            <input
              type="text"
              placeholder="Search by voucher code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border-0 focus:ring-2 focus:ring-purple-500 text-sm sm:text-base outline-none"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm form-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="deleted">Deleted</option>
        </select>
        <button
          onClick={() => handleShowModal()}
          className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
        >
          <FaPlus className="w-4 h-4" />
          <span className="whitespace-nowrap">Add Voucher</span>
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-8 border-purple-600 border-t-transparent"></div>
        </div>
      ) : filteredVouchers.length === 0 ? (
        <div className="text-center py-12 rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            {searchTerm || statusFilter !== 'all'
              ? 'No vouchers found matching your criteria.'
              : 'No vouchers yet. Click "Add Voucher" to create one.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">Code</th>
                <th className="py-3 px-3 text-left font-bold">Scope</th>
                <th className="py-3 px-3 text-left font-bold">Course</th>
                <th className="py-3 px-3 text-left font-bold">Discount</th>
                <th className="py-3 px-3 text-left font-bold">Validity</th>
                <th className="py-3 px-3 text-left font-bold">Usage</th>
                <th className="py-3 px-3 text-center font-bold">Status</th>
                <th className="py-3 px-3 text-center font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id} className={`border-b border-purple-100 hover:bg-purple-50/50 transition-all ${voucher.deleted ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-3 font-bold text-purple-800">{voucher.code}</td>
                  <td className="py-3 px-3">{voucher.scope === 'global' ? 'Global' : 'Course'}</td>
                  <td className="py-3 px-3">{voucher.scope === 'course' ? getCourseName(voucher.course_id) : '-'}</td>
                  <td className="py-3 px-3">
                    {voucher.discount_type === 'percentage'
                      ? `${voucher.discount_value}% Off`
                      : `Flat ₹${voucher.discount_value}`}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600">
                    <div>From: {voucher.start_date ? new Date(voucher.start_date).toLocaleString() : '-'}</div>
                    <div>To: {voucher.end_date ? new Date(voucher.end_date).toLocaleString() : '-'}</div>
                  </td>
                  <td className="py-3 px-3 text-xs">
                    <div>Used: {voucher.used_count || 0}</div>
                    <div>Limit: {voucher.usage_limit != null ? voucher.usage_limit : 'Unlimited'}</div>
                    <div>Per user: {voucher.limit_per_user}</div>
                  </td>
                  <td className="py-3 px-3 text-center">
                    {voucher.deleted ? (
                      <span className="bg-red-100 text-red-800 px-4 py-2 rounded-full font-bold text-xs">Deleted</span>
                    ) : (
                      <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-bold ${voucher.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                        {voucher.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <div className="d-flex gap-1 justify-content-center">
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => handleShowModal(voucher)}
                        title="Edit Voucher"
                        className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      <Button
                        bsPrefix="custom-hamburger"
                        onClick={() => navigate(`/admin/vouchers/${voucher.id}/usage`)}
                        title="View Usage"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded p-1"
                      >
                        <FaList className="w-5 h-5" />
                      </Button>
                      {!voucher.deleted ? (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={() => handleDeleteClick(voucher)}
                          title="Delete Voucher"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      ) : (
                        <Button
                          bsPrefix="custom-hamburger"
                          onClick={async () => {
                            try {
                              await api.vouchers.restore(voucher.id);
                              toast.success('Voucher restored successfully');
                              await fetchVouchers();
                            } catch (error) {
                              toast.error(error.response?.data?.message || 'Failed to restore');
                            }
                          }}
                          title="Restore Voucher"
                          className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1"
                        >
                          <FaUndo className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            {editingVoucher ? 'Edit Voucher' : 'Create New Voucher'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          <form id="voucherForm" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Voucher Code <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="code"
                  value={formState.code}
                  onChange={handleInputChange}
                  disabled={submitting}
                  className={`w-full px-3 py-2 rounded-lg border ${formErrors.code ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200 outline-none`}
                />
                {formErrors.code && <div className="text-red-500 text-xs mt-1">{formErrors.code}</div>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Scope</label>
                <select name="scope" value={formState.scope} onChange={handleInputChange} disabled={submitting} className="rounded-lg  sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
                  <option value="global">Global</option>
                  <option value="course">Course Specific</option>
                </select>
              </div>

              {formState.scope === 'course' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Course <span className="text-red-500">*</span></label>
                  <select name="course_id" value={formState.course_id} onChange={handleInputChange} disabled={submitting} className={`w-full px-3 py-2 rounded-lg border ${formErrors.course_id ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200`}>
                    <option value="">Select Course</option>
                    {courses.map(c => (<option key={c.id} value={c.id}>{c.title || c.name}</option>))}
                  </select>
                  {formErrors.course_id && <div className="text-red-500 text-xs mt-1">{formErrors.course_id}</div>}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Discount Type</label>
                <select name="discount_type" value={formState.discount_type} onChange={handleInputChange} disabled={submitting} className="rounded-lg  sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
                  <option value="percentage">Percentage (%)</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Discount Value <span className="text-red-500">*</span></label>
                <input type="number" name="discount_value" value={formState.discount_value} onChange={handleInputChange} disabled={submitting} min="0" step="0.01" className={`w-full px-3 py-2 rounded-lg border ${formErrors.discount_value ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200`} />
                {formErrors.discount_value && <div className="text-red-500 text-xs mt-1">{formErrors.discount_value}</div>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Start Date & Time <span className="text-red-500">*</span></label>
                <input type="datetime-local" name="start_date" value={formState.start_date} onChange={handleInputChange} disabled={submitting} className={`w-full px-3 py-2 rounded-lg border ${formErrors.start_date ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200`} />
                {formErrors.start_date && <div className="text-red-500 text-xs mt-1">{formErrors.start_date}</div>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">End Date & Time <span className="text-red-500">*</span></label>
                <input type="datetime-local" name="end_date" value={formState.end_date} onChange={handleInputChange} disabled={submitting} className={`w-full px-3 py-2 rounded-lg border ${formErrors.end_date ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200`} />
                {formErrors.end_date && <div className="text-red-500 text-xs mt-1">{formErrors.end_date}</div>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Usage Limit (optional)</label>
                <input type="number" name="usage_limit" value={formState.usage_limit} onChange={handleInputChange} disabled={submitting} min="1" placeholder="Unlimited if empty" className="w-full px-3 py-2 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-200" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Limit Per User <span className="text-red-500">*</span></label>
                <input type="number" name="limit_per_user" value={formState.limit_per_user} onChange={handleInputChange} disabled={submitting} min="1" className={`w-full px-3 py-2 rounded-lg border ${formErrors.limit_per_user ? 'border-red-500' : 'border-purple-300'} focus:ring-2 focus:ring-purple-200`} />
                {formErrors.limit_per_user && <div className="text-red-500 text-xs mt-1">{formErrors.limit_per_user}</div>}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                <select name="status" value={formState.status} onChange={handleInputChange} disabled={submitting} className="rounded-lg  sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

          </form>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseModal}
                disabled={submitting}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                form="voucherForm"
                disabled={submitting}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {editingVoucher ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingVoucher ? 'Update Voucher' : 'Create Voucher'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Voucher</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{voucherToDelete?.code}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-3">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Voucher'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VoucherManagement;